import { createDeck, cardId } from './cards';
import { getCaptureOptions } from './captureOptions';
import { computeHandScore, computeHandScoreSides } from './scoring';

// ===== Game Engine =====
// Pure, UI-independent state machine for classic Scopa (1 vs 1).
// State is fully serializable (localStorage friendly) and designed so a
// networked multiplayer layer could drive it in the future without rewrites.

export const TARGET_SCORE = 11;

function shuffle(arr, rng = Math.random) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function other(actor) {
  return actor === 'player' ? 'cpu' : 'player';
}

// Deal a fresh hand (mano). dealer deals; the non-dealer plays first.
function dealHand(dealer) {
  const deck = shuffle(createDeck());
  const table = deck.splice(0, 4);
  const nonDealer = other(dealer);
  const hands = { player: [], cpu: [] };
  // 3 cards each, starting with the non-dealer
  hands[nonDealer] = deck.splice(0, 3);
  hands[dealer] = deck.splice(0, 3);
  return {
    deck,
    table,
    hands,
    captured: { player: [], cpu: [] },
    scope: { player: 0, cpu: 0 },
    turn: nonDealer,
    lastCapturer: null,
  };
}

export function newGame() {
  const dealer = 'cpu'; // human (non-dealer) plays first
  const hand = dealHand(dealer);
  return {
    ...hand,
    dealer,
    handNumber: 1,
    matchScores: { player: 0, cpu: 0 },
    phase: 'playing', // 'playing' | 'handOver' | 'gameOver'
    lastHandBreakdown: null,
    winner: null,
  };
}

// Expose capture options for a given hand card of an actor.
export function optionsForCard(state, actor, cId) {
  const card = state.hands[actor].find((c) => c.id === cId);
  if (!card) return [];
  return getCaptureOptions(card, state.table);
}

// Apply a move. captureCardIds is an array of table card ids to capture (or []/null to discard).
// Returns { state, events } where events describe what happened (for narrator/audio/animation).
export function applyMove(state, actor, cId, captureCardIds) {
  if (state.phase !== 'playing') return { state, events: [] };
  const hand = state.hands[actor];
  const card = hand.find((c) => c.id === cId);
  if (!card) return { state, events: [] };

  const newHand = hand.filter((c) => c.id !== cId);
  const cardsLeftAfter = newHand.length + state.hands[other(actor)].length;
  const isLastPlay = state.deck.length === 0 && cardsLeftAfter === 0;

  let table = [...state.table];
  const captured = {
    player: [...state.captured.player],
    cpu: [...state.captured.cpu],
  };
  const scope = { ...state.scope };
  let lastCapturer = state.lastCapturer;

  const events = [];
  const captureSet = captureCardIds && captureCardIds.length > 0
    ? new Set(captureCardIds)
    : null;

  let capturedCards = [];
  let scopa = false;

  if (captureSet) {
    capturedCards = table.filter((c) => captureSet.has(c.id));
    table = table.filter((c) => !captureSet.has(c.id));
    captured[actor].push(...capturedCards, card);
    lastCapturer = actor;
    if (table.length === 0 && !isLastPlay) {
      scopa = true;
      scope[actor] += 1;
    }
    events.push({
      type: 'capture',
      actor,
      played: card,
      captured: capturedCards,
      scopa,
    });
  } else {
    table.push(card);
    events.push({ type: 'play', actor, played: card });
  }

  const hands = { ...state.hands, [actor]: newHand };
  let next = {
    ...state,
    hands,
    table,
    captured,
    scope,
    lastCapturer,
    turn: other(actor),
  };

  // Refill when both hands are empty and deck still has cards.
  if (hands.player.length === 0 && hands.cpu.length === 0) {
    if (next.deck.length > 0) {
      const deck = [...next.deck];
      const nonDealer = other(next.dealer);
      const newHands = { player: [], cpu: [] };
      newHands[nonDealer] = deck.splice(0, 3);
      newHands[next.dealer] = deck.splice(0, 3);
      next = { ...next, deck, hands: newHands };
      events.push({ type: 'refill' });
    } else {
      // End of hand: remaining table cards go to the last capturer.
      next = resolveHand(next, events);
    }
  }

  return { state: next, events };
}

function resolveHand(state, events) {
  const captured = {
    player: [...state.captured.player],
    cpu: [...state.captured.cpu],
  };
  if (state.table.length > 0 && state.lastCapturer) {
    captured[state.lastCapturer].push(...state.table);
  }
  const breakdown = computeHandScore(captured, state.scope);
  const matchScores = {
    player: state.matchScores.player + breakdown.player.points,
    cpu: state.matchScores.cpu + breakdown.cpu.points,
  };

  events.push({ type: 'handOver', breakdown, matchScores });

  // Game over check: someone reached target AND there's a unique leader.
  const reached =
    matchScores.player >= TARGET_SCORE || matchScores.cpu >= TARGET_SCORE;
  const uniqueLeader = matchScores.player !== matchScores.cpu;

  if (reached && uniqueLeader) {
    const winner = matchScores.player > matchScores.cpu ? 'player' : 'cpu';
    events.push({ type: 'gameOver', winner, matchScores });
    return {
      ...state,
      table: [],
      captured,
      matchScores,
      phase: 'gameOver',
      lastHandBreakdown: breakdown,
      winner,
    };
  }

  return {
    ...state,
    table: [],
    captured,
    matchScores,
    phase: 'handOver',
    lastHandBreakdown: breakdown,
  };
}

// Start the next hand after a 'handOver' pause (dealer alternates).
export function startNextHand(state) {
  const dealer = other(state.dealer);
  const hand = dealHand(dealer);
  return {
    ...state,
    ...hand,
    dealer,
    handNumber: state.handNumber + 1,
    phase: 'playing',
    lastHandBreakdown: null,
  };
}

export { other, cardId };

// ===== 2-4 players (online) =====
// Separate entry points so the 1v1 functions above (CPU mode + current online)
// stay byte-for-byte unchanged. Seats play in order A -> B -> C -> D.
// 3 players: individual scores. 4 players: teams AC vs BD share one score.
const SEATS = ['A', 'B', 'C', 'D'];
const TEAM_OF = { A: 'AC', C: 'AC', B: 'BD', D: 'BD' };

export function seatsFor(numPlayers) {
  if (![2, 3, 4].includes(numPlayers)) throw new Error('numPlayers must be 2, 3 or 4');
  return SEATS.slice(0, numPlayers);
}

// Scoring sides: teams with 4 players, otherwise each seat on its own.
export function sidesFor(numPlayers) {
  return numPlayers === 4 ? ['AC', 'BD'] : seatsFor(numPlayers);
}

export function sideOf(numPlayers, seat) {
  return numPlayers === 4 ? TEAM_OF[seat] : seat;
}

function nextSeat(players, seat) {
  return players[(players.indexOf(seat) + 1) % players.length];
}

function perSeat(players, make) {
  return Object.fromEntries(players.map((p) => [p, make(p)]));
}

// 3 cards each, starting from the player after the dealer.
function dealRound(players, dealer, deck) {
  const hands = perSeat(players, () => []);
  let p = nextSeat(players, dealer);
  for (let i = 0; i < players.length; i++) {
    hands[p] = deck.splice(0, 3);
    p = nextSeat(players, p);
  }
  return hands;
}

function dealHandMulti(players, dealer) {
  const deck = shuffle(createDeck());
  const table = deck.splice(0, 4);
  const hands = dealRound(players, dealer, deck);
  return {
    deck,
    table,
    hands,
    captured: perSeat(players, () => []),
    scope: perSeat(players, () => 0),
    turn: nextSeat(players, dealer),
    lastCapturer: null,
  };
}

// Dealer is the last seat, so seat A plays first (as the human does in 1v1).
export function newGameMulti(numPlayers) {
  const players = seatsFor(numPlayers);
  const dealer = players[players.length - 1];
  return {
    ...dealHandMulti(players, dealer),
    numPlayers,
    players,
    sides: sidesFor(numPlayers),
    dealer,
    handNumber: 1,
    matchScores: Object.fromEntries(sidesFor(numPlayers).map((s) => [s, 0])),
    phase: 'playing',
    lastHandBreakdown: null,
    winner: null, // a side: seat (2-3 players) or team 'AC' | 'BD'
  };
}

// Same contract as applyMove (actor = seat). Does NOT validate capture
// legality: callers check it with optionsForCard, exactly as today.
export function applyMoveMulti(state, actor, cId, captureCardIds) {
  if (state.phase !== 'playing' || state.turn !== actor) return { state, events: [] };
  const hand = state.hands[actor];
  const card = hand.find((c) => c.id === cId);
  if (!card) return { state, events: [] };

  const { players } = state;
  const newHand = hand.filter((c) => c.id !== cId);
  const hands = { ...state.hands, [actor]: newHand };
  const allEmpty = players.every((p) => hands[p].length === 0);
  const isLastPlay = state.deck.length === 0 && allEmpty;

  let table = [...state.table];
  const captured = perSeat(players, (p) => [...state.captured[p]]);
  const scope = { ...state.scope };
  let lastCapturer = state.lastCapturer;
  const events = [];
  const captureSet = captureCardIds && captureCardIds.length > 0 ? new Set(captureCardIds) : null;

  if (captureSet) {
    const capturedCards = table.filter((c) => captureSet.has(c.id));
    table = table.filter((c) => !captureSet.has(c.id));
    captured[actor].push(...capturedCards, card);
    lastCapturer = actor;
    let scopa = false;
    if (table.length === 0 && !isLastPlay) {
      scopa = true;
      scope[actor] += 1;
    }
    events.push({ type: 'capture', actor, played: card, captured: capturedCards, scopa });
  } else {
    table.push(card);
    events.push({ type: 'play', actor, played: card });
  }

  let next = { ...state, hands, table, captured, scope, lastCapturer, turn: nextSeat(players, actor) };

  if (allEmpty) {
    if (next.deck.length > 0) {
      const deck = [...next.deck];
      next = { ...next, deck, hands: dealRound(players, next.dealer, deck), turn: nextSeat(players, next.dealer) };
      events.push({ type: 'refill' });
    } else {
      next = resolveHandMulti(next, events);
    }
  }
  return { state: next, events };
}

function resolveHandMulti(state, events) {
  const { players, sides, numPlayers } = state;
  const captured = perSeat(players, (p) => [...state.captured[p]]);
  if (state.table.length > 0 && state.lastCapturer) {
    captured[state.lastCapturer].push(...state.table);
  }
  const bySide = Object.fromEntries(sides.map((s) => [s, []]));
  const scopeBySide = Object.fromEntries(sides.map((s) => [s, 0]));
  for (const p of players) {
    const s = sideOf(numPlayers, p);
    bySide[s].push(...captured[p]);
    scopeBySide[s] += state.scope[p];
  }
  const breakdown = computeHandScoreSides(bySide, scopeBySide, sides);
  const matchScores = Object.fromEntries(sides.map((s) => [s, state.matchScores[s] + breakdown[s].points]));
  events.push({ type: 'handOver', breakdown, matchScores });

  // Game over: someone reached the target AND there is a unique leader.
  const max = Math.max(...sides.map((s) => matchScores[s]));
  const leaders = sides.filter((s) => matchScores[s] === max);
  const base = { ...state, table: [], captured, matchScores, lastHandBreakdown: breakdown };
  if (max >= TARGET_SCORE && leaders.length === 1) {
    events.push({ type: 'gameOver', winner: leaders[0], matchScores });
    return { ...base, phase: 'gameOver', winner: leaders[0] };
  }
  return { ...base, phase: 'handOver' };
}

// Next hand: the deal passes to the next seat.
export function startNextHandMulti(state) {
  const dealer = nextSeat(state.players, state.dealer);
  return {
    ...state,
    ...dealHandMulti(state.players, dealer),
    dealer,
    handNumber: state.handNumber + 1,
    phase: 'playing',
    lastHandBreakdown: null,
  };
}
